import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVehicle, clearVehicleSelection } from '../../context/VehicleContext';

// Maps the v11 "combined" vehicle key prefixes to the legacy akku-24 step-1 tiles.
const TYPE_META: Record<string, { label: string; icon: string }> = {
	car: { label: 'Személyautó', icon: '/img/icons/gjkereso_step1_auto.png' },
	motorcycle: { label: 'Motorkerékpár', icon: '/img/icons/gjkereso_step1_motor.png' },
	van: { label: 'Kisteherautó', icon: '/img/icons/gjkereso_step1_kisteherauto.png' },
};
const TYPE_ORDER = ['car', 'motorcycle', 'van'];
const FUEL_LABELS: Record<string, string> = {
	petrol: 'Benzin', diesel: 'Dízel', hybrid: 'Hibrid', electric: 'Elektromos',
};
const FUEL_ORDER = ['petrol', 'diesel', 'hybrid', 'electric'];

interface Props {
	prompt?: string;
	/** Where to go once a concrete vehicle is picked. Defaults to /akkuvalaszto. */
	target?: string;
}

export default function Gjkereso({ prompt = 'Az akkumulátorod megtalálásához válaszd ki gépjárműved típusát:', target = '/akkuvalaszto' }: Props) {
	const v = useVehicle();
	const navigate = useNavigate();
	// vehicle-type prefix (car/motorcycle/van); derived from the persisted combined key on mount.
	const [vtype, setVtype] = useState<string>(() => v.selectedFuelType.split('_')[0] || '');

	const availableTypes = TYPE_ORDER.filter((t) => v.fuelTypeOptions.some((o) => o.value.startsWith(t + '_')));
	const availableFuels = vtype
		? FUEL_ORDER.filter((f) => v.fuelTypeOptions.some((o) => o.value === `${vtype}_${f}`))
		: [];
	const fuel = v.selectedFuelType.split('_')[1] || '';

	function pickType(t: string) {
		setVtype(t);
		clearVehicleSelection();
	}
	function pickFuel(f: string) {
		v.setSelectedFuelType(`${vtype}_${f}`);
	}
	function reset() {
		setVtype('');
		clearVehicleSelection();
	}

	return (
		<section className="gjkereso">
			<div className="gjkereso__inner">
				<p className="gjkereso__prompt">{prompt}</p>

				{/* step 1 — vehicle type */}
				<div className="gjk-types">
					{(availableTypes.length ? availableTypes : TYPE_ORDER).map((t) => (
						<button
							key={t}
							type="button"
							className={`gjk-type ${vtype === t ? 'is-selected' : ''}`}
							title={TYPE_META[t].label}
							onClick={() => pickType(t)}
						>
							<img src={TYPE_META[t].icon} alt={TYPE_META[t].label} />
						</button>
					))}
				</div>

				{/* step 2 — fuel */}
				{vtype && (
					<div className="gjkereso__row" style={{ marginTop: 28 }}>
						<div className="gjk-fuels">
							{availableFuels.map((f) => (
								<button
									key={f}
									type="button"
									className={`gjk-fuel ${fuel === f ? 'is-selected' : ''}`}
									onClick={() => pickFuel(f)}
								>
									{FUEL_LABELS[f]}
								</button>
							))}
						</div>
					</div>
				)}

				{/* step 3 — brand / model / type selects */}
				{v.selectedFuelType && (
					<div className="gjk-selects" style={{ marginTop: 28 }}>
						<select
							className="gjk-select"
							value={v.selectedBrand}
							onChange={(e) => v.setSelectedBrand(e.target.value)}
						>
							<option value="">Márka</option>
							{v.brandOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
						</select>

						<select
							className="gjk-select"
							value={v.selectedModel}
							disabled={!v.selectedBrand}
							onChange={(e) => v.setSelectedModel(e.target.value)}
						>
							<option value="">Típus</option>
							{v.modelOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
						</select>

						<select
							className="gjk-select"
							value={v.selectedType}
							disabled={!v.selectedModel}
							onChange={(e) => {
								v.setSelectedType(e.target.value);
								if (e.target.value) navigate(target);
							}}
						>
							<option value="">Motorizáció</option>
							{v.typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
						</select>
					</div>
				)}

				{(vtype || v.selectedFuelType) && (
					<button type="button" className="gjk-back" onClick={reset}>← Újrakezdés</button>
				)}
			</div>
		</section>
	);
}
